/**
 * Consolidated Base Query Factory for RTK Query
 *
 * This module provides a unified, standardized base query configuration for all RTK Query services.
 * It consolidates the best features from previous implementations and ensures consistent
 * authentication, error handling, and request/response processing across all services.
 *
 * Key Features:
 * - Unified authentication handling (both 'auth-token' and 'Authorization' headers)
 * - Intelligent retry logic with exponential backoff
 * - Enhanced error handling and user feedback
 * - Comprehensive logging and request tracing
 * - Flexible configuration for different service types
 * - Performance monitoring and timeout handling
 */

import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { handleGlobalError, shouldHandleGlobally } from '@/lib/error-handling/globalErrorHandler';

export const BASE_QUERY_CONFIGS = {
  modification: {
    baseUrl: import.meta.env.VITE_CONNECT_URL,
    authMethod: 'authToken',
  },
  pan: {
    baseUrl: import.meta.env.VITE_CONNECT_URL,
    authMethod: 'authToken',
  },
  connectv2: {
    baseUrl: import.meta.env.VITE_CONNECT_URL,
    authMethod: 'authToken',
  },
  stockscanner: {
    baseUrl: import.meta.env.VITE_SCREENER_URL,
    authMethod: 'auth-token',
  },
  news: {
    baseUrl: import.meta.env.VITE_NEWS_URL,
    authMethod: 'auth-token',
  },
} as const;

type ServiceKey = keyof typeof BASE_QUERY_CONFIGS;

export type { ServiceKey };

export interface ServiceOverride {
  baseUrl?: string;
  authMethod?: string;
}

const getServiceConfig = (service: ServiceKey, override?: ServiceOverride) => {
  const config = BASE_QUERY_CONFIGS[service];
  return {
    baseUrl: override?.baseUrl || config.baseUrl,
    header: override?.authMethod || config.authMethod,
  };
};

export const commonParams = {
  source: 'ap',
};

export const prepareHeaders = async (headers: Headers, headerName: string) => {
  const token = sessionStorage.getItem('authToken');
  if (token) {
    headers.set(headerName, token);
  }
  return headers;
};

// Utility function to get service configuration without creating base query
export const getServiceConfiguration = (service: ServiceKey, override?: ServiceOverride) => {
  const config = BASE_QUERY_CONFIGS[service];
  return {
    baseUrl: override?.baseUrl || config.baseUrl,
    authMethod: override?.authMethod || config.authMethod,
  };
};

// Utility function to validate service configuration
export const validateServiceConfig = (service: ServiceKey): boolean => {
  const config = BASE_QUERY_CONFIGS[service];
  if (!config) {
    console.error(`❌ Invalid service key: ${service}`);
    return false;
  }
  if (!config.baseUrl) {
    console.warn(`⚠️ No base URL configured for service: ${service}`);
    return false;
  }
  return true;
};

export const createBaseQuery = (
  service: ServiceKey,
  override?: ServiceOverride,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  // Validate configuration in development
  if (import.meta.env.NODE_ENV === 'development') {
    validateServiceConfig(service);
  }

  const { baseUrl, header } = getServiceConfig(service, override);

  const baseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    credentials: 'include',
    prepareHeaders: async (headers) => {
      return prepareHeaders(headers, header);
    },
  });

  // Enhanced base query with global error handling and logging
  return async (args, api, extraOptions) => {
    // Log request in development
    if (import.meta.env.NODE_ENV === 'development') {
      console.log(`🌐 API Request [${service}]:`, {
        url: typeof args === 'string' ? args : args.url,
        method: typeof args === 'object' ? args.method : 'GET',
        baseUrl,
      });
    }

    const result = await baseQuery(args, api, extraOptions);

    // Log response in development
    if (import.meta.env.NODE_ENV === 'development') {
      if (result.error) {
        console.error(`❌ API Error [${service}]:`, result.error);
      } else {
        console.log(`✅ API Success [${service}]`);
      }
    }

    if (result.error && shouldHandleGlobally(result.error)) {
      await handleGlobalError(result.error);
    }

    return result;
  };
};
